import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { orgSettingsApi } from '../features/orgSettings/api.ts';

function StampCard({ hasStamp }: { hasStamp: boolean }): JSX.Element {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    if (hasStamp) {
      void orgSettingsApi.fetchStampBlobUrl().then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        url = u;
        setPreviewUrl(u);
      }).catch(() => undefined);
    } else {
      setPreviewUrl(null);
    }
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [hasStamp]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org'] });

  const upload = useMutation({
    mutationFn: (file: File) => orgSettingsApi.uploadStamp(file),
    onSuccess: () => { void invalidate(); },
  });
  const remove = useMutation({
    mutationFn: () => orgSettingsApi.removeStamp(),
    onSuccess: () => { void invalidate(); },
  });

  return (
    <section className="card-paper space-y-3 p-4">
      <h2 className="status-label text-muted-foreground flex items-center gap-2"><Icon name="approval" size={15} /> Official stamp</h2>
      {hasStamp && previewUrl && (
        <img
          src={previewUrl}
          alt="Organization stamp"
          className="max-h-32 rounded-md border border-border bg-white object-contain p-2"
        />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
          className="btn-primary btn-sm"
        >
          {upload.isPending ? 'Uploading…' : hasStamp ? 'Replace stamp' : 'Upload stamp'}
        </button>
        {hasStamp && (
          <button
            type="button"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
            className="btn-secondary btn-sm"
          >
            Remove
          </button>
        )}
        {upload.isError && <span className="text-sm text-destructive">Upload failed. Please try again.</span>}
        {remove.isError && <span className="text-sm text-destructive">Could not remove the stamp.</span>}
      </div>
      <p className="text-xs text-muted-foreground">PNG or JPEG, max 2MB, transparent background recommended.</p>
    </section>
  );
}

export function OrgSettings(): JSX.Element {
  const qc = useQueryClient();
  const { data: org, isLoading } = useQuery({ queryKey: ['org'], queryFn: orgSettingsApi.get });

  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (org && !initialized) {
      setName(org.name);
      setContactEmail(org.contactEmail);
      setInitialized(true);
    }
  }, [org, initialized]);

  const save = useMutation({
    mutationFn: () => orgSettingsApi.patch({ name: name.trim(), contactEmail: contactEmail.trim() }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['org'] }); },
  });

  if (isLoading || !org) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-6">
      <section className="card-paper space-y-3 p-4">
        <h2 className="status-label text-muted-foreground">Organization details</h2>
        <form
          className="space-y-4"
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) save.mutate(); }}
        >
          <div className="space-y-1.5">
            <label htmlFor="org-name" className="field-label">Organization name</label>
            <input
              id="org-name"
              type="text"
              value={name}
              maxLength={120}
              required
              onChange={(e) => setName(e.target.value)}
              className="input"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="org-email" className="field-label">Contact email</label>
            <input
              id="org-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="input"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={save.isPending || !name.trim()}
              className="btn-primary"
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </button>
            {save.isSuccess && <span className="pill pill-success">Saved</span>}
            {save.isError && <span className="text-sm text-destructive">Could not save. Check the fields and try again.</span>}
          </div>
        </form>
      </section>

      <StampCard hasStamp={org.hasStamp} />
    </div>
  );
}
